import { BESOKSSTATISTIKK_MAANEDER, KLIKKID_DAGER } from './lagringstid';
import { harAnnonsemaaling, harStatistikk } from './informasjonskapsler';

/**
 * Målingen, sett fra nettleseren.
 *
 * To ting skjer her: vi melder fra om at en side ble sett, og vi bærer med oss
 * hvor de kom fra fram til registreringen.
 *
 * **Tellingen lagrer aldri noe på maskinen.** Den er en teller – dato, steg,
 * kanal, enhet – uten identifikator, og det er nettopp fraværet av lagring som
 * gjør at den ikke krever samtykke etter ekomloven § 3-15.
 *
 * **Kampanjen leses av adressen og holdes i minnet.** Å lese en parameter vi
 * fikk servert, er ikke å lagre noe hos den besøkende. Derfor gjør vi det
 * uansett svar: uten det ville den som sier ja *etter* at siden lastet, mistet
 * kampanjen sin i samme sekund.
 *
 * **Henvisningen leses av `document.referrer` og holdes i minnet.** Det er
 * den som avgjør kanalen – Google, sosiale medier, andre nettsteder – og den
 * finnes ikke noe annet sted. Bare verten sendes, og den lagres aldri.
 *
 * **Klikk-ID-en skrives ikke ned uten samtykke.** `gclid` og `fbclid` finnes
 * for å meldes tilbake til annonsenettverket som en konvertering, og juridisk
 * gjennomgang landet på at det gjør dem til noe annet enn en vanlig
 * kundeopplysning. Uten et ja: ingenting i `localStorage`, og ingen id sendt
 * med registreringen. Med et ja: kampanjen overlever at fanen lukkes, som er
 * hele det samtykket kjøper.
 *
 * Kanal og kampanjenavn står uansett. De sier at noen kom fra en kampanje,
 * aldri hvilket klikk, og hviler på berettiget interesse.
 *
 * **Besøks-ID-en lages bare med samtykke til besøksstatistikk.** Den er en
 * tilfeldig UUID i `localStorage`, sendt med målingen av de åpne sidene, og det
 * er den som lar oss telle personer i stedet for sidevisninger. Uten et ja
 * finnes den ikke, og tellingen er like fri for lagring som før.
 */

/** Hvor kampanjen ligger når den besøkende har sagt ja til å bli målt. */
const LAGER = 'vaardag:kampanje';

/** Hvor besøks-ID-en ligger når den besøkende har sagt ja til besøksstatistikk. */
const BESOKSLAGER = 'vaardag:besokende';

export type Klientsteg = 'forside' | 'priser' | 'registrering_apnet' | 'abonnement_apnet';

/**
 * Stegene som tar med seg besøks-ID-en. Speiling av `BESOKSSTEG` i API-et.
 *
 * Bare de åpne sidene: abonnementssida er inne i appen, og hører ikke hjemme i
 * en oversikt over hvem som kommer innom.
 */
const BESOKSSTEG: readonly Klientsteg[] = ['forside', 'priser', 'registrering_apnet'];

/** Speiling av `shared/trakt.ts` i API-et, som er den som gjelder. */
export type Steg =
  | 'forside'
  | 'registrering_apnet'
  | 'konto_opprettet'
  | 'bryllup_opprettet'
  | 'gjest_lagt_inn'
  | 'abonnement_apnet'
  | 'kjop_startet'
  | 'kjop_fullfort';

export const STEGNAVN: Record<Steg, string> = {
  forside: 'Forside eller priser',
  registrering_apnet: 'Registrering åpnet',
  konto_opprettet: 'Konto opprettet',
  bryllup_opprettet: 'Bryllup opprettet',
  gjest_lagt_inn: 'Gjest lagt inn',
  abonnement_apnet: 'Abonnement åpnet',
  kjop_startet: 'Kjøp startet',
  kjop_fullfort: 'Kjøp fullført',
};

export type Kanal = 'google' | 'direkte' | 'sosialt' | 'annet' | 'kampanje';

export const KANALNAVN: Record<Kanal, string> = {
  google: 'Google-søk',
  direkte: 'Direkte',
  sosialt: 'Sosiale medier',
  annet: 'Andre nettsteder',
  kampanje: 'Kampanje',
};

export type Kampanje = {
  /** `utm_source`: hvor lenken ble delt. */
  kilde?: string;
  /** `utm_campaign`: navnet på annonsen eller innlegget. */
  navn?: string;
  /** `gclid` fra Google Ads eller `fbclid` fra Meta. */
  klikkid?: string;
};

let husket: Kampanje | null = null;

/** Kutter en parameter til noe som ikke sprenger noe lenger ute. */
function kort(verdi: string | null, lengde: number): string | undefined {
  const rent = (verdi ?? '').trim().slice(0, lengde);
  return rent === '' ? undefined : rent;
}

/**
 * Det som ligger lagret fra et tidligere besøk, hvis vi har lov til å se det.
 *
 * Fristen sjekkes før samtykket. Når beskjeden får en ny versjon, er det gamle
 * ja-et ikke lenger lesbart – men kampanjen det lagret, ligger der fortsatt, og
 * den skal ikke bli liggende over fristen fordi noen lar den nye boksen stå
 * ubesvart.
 */
function fraLageret(): Kampanje | null {
  try {
    const raa = window.localStorage.getItem(LAGER);
    if (!raa) return null;
    const lagret = JSON.parse(raa) as Partial<Kampanje> & { tidspunkt?: string };
    // Samme frist som serveren gir klikk-ID-en. En id som er for gammel til å
    // brukes, skal ikke bli liggende i nettleseren og se nyttig ut.
    const alder = Date.now() - Date.parse(String(lagret.tidspunkt ?? ''));
    if (!Number.isFinite(alder) || alder > KLIKKID_DAGER * 86_400_000) {
      window.localStorage.removeItem(LAGER);
      return null;
    }
    if (!harAnnonsemaaling()) return null;
    return { kilde: lagret.kilde, navn: lagret.navn, klikkid: lagret.klikkid };
  } catch {
    return null;
  }
}

/**
 * Kampanjen besøket kom inn på.
 *
 * Leses én gang, fra adressen sida ble lastet med, og huskes deretter i minnet.
 * Derfor overlever den at brukeren klikker seg fra forsiden til registreringen
 * — det er én og samme sidelasting i en enkeltsideapplikasjon.
 *
 * Står det ingenting i adressen, ser vi etter noe lagret fra et tidligere
 * besøk. Det finnes bare når den besøkende har sagt ja.
 */
export function kampanje(): Kampanje {
  if (husket) return husket;
  try {
    const p = new URLSearchParams(window.location.search);
    const fraAdressen: Kampanje = {
      kilde: kort(p.get('utm_source'), 60),
      navn: kort(p.get('utm_campaign'), 80),
      klikkid: kort(p.get('gclid') ?? p.get('fbclid'), 200),
    };
    const tom = !fraAdressen.kilde && !fraAdressen.navn && !fraAdressen.klikkid;
    husket = tom ? (fraLageret() ?? fraAdressen) : fraAdressen;
  } catch {
    husket = {};
  }
  return husket;
}

/**
 * Skriver kampanjen ned, slik at den overlever at fanen lukkes.
 *
 * Kalles når noen sier ja, og bare da. Er det ingenting å huske, skrives
 * ingenting: en tom rad i lageret er en rad som må ryddes senere uten å ha
 * gjort nytte.
 */
export function huskKampanje(): void {
  const k = kampanje();
  if (!k.kilde && !k.navn && !k.klikkid) return;
  try {
    window.localStorage.setItem(LAGER, JSON.stringify({ ...k, tidspunkt: new Date().toISOString() }));
  } catch {
    /* Får vi ikke lagre, lever kampanjen fanen ut. Ikke verdt en feilmelding. */
  }
}

/** `undefined` betyr lest og tom; `null` betyr ikke lest ennå. */
let henvist: string | undefined | null = null;

/**
 * Verten besøket kom fra, eller ingenting.
 *
 * Kanalen kan bare avgjøres her. `Referer`-headeren på våre egne kall er
 * tom – Helmet setter `Referrer-Policy: no-referrer` – og ville uansett bare
 * sagt vardag.no. Søket eller innlegget de kom fra, står bare i
 * `document.referrer` på sida de landet på.
 *
 * Den endrer seg ikke når appen navigerer, så den kan leses ved første
 * melding og huskes i minnet, akkurat som kampanjen. Den skrives aldri til
 * lageret, heller ikke med samtykke: da ville tellingen ikke lenger vært fri
 * for lagring, og det er det som holder den utenfor ekomloven § 3-15.
 *
 * Bare verten går ut av maskinen. Stien kan bære søkeordene, og kanalen
 * trenger dem ikke.
 */
export function henvisning(): string | undefined {
  if (henvist !== null) return henvist;
  try {
    const vert = new URL(window.document.referrer).hostname;
    henvist = vert !== '' && vert.length <= 253 ? vert : undefined;
  } catch {
    henvist = undefined;
  }
  return henvist;
}

/** En tilfeldig UUID, også i nettlesere uten `crypto.randomUUID`. */
function nyId(): string {
  const krypto = globalThis.crypto;
  if (typeof krypto?.randomUUID === 'function') return krypto.randomUUID();
  const b = krypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const hex = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Besøks-ID-en, eller ingenting.
 *
 * Bare med et ja til besøksstatistikk. Da lages den første gang den trengs, og
 * byttes ut når den er eldre enn fristen: serveren sletter besøkene som hører
 * til en så gammel ID, og en ID som lever videre i nettleseren etter det, ville
 * sett ut som en ny besøkende som kjenner oss fra før.
 *
 * Får vi ikke lese eller skrive lageret, er det heller ingen ID. En besøkende
 * vi ikke kan kjenne igjen, telles som en sum, som alle andre.
 */
export function besoksId(): string | undefined {
  if (!harStatistikk()) return undefined;
  try {
    const raa = window.localStorage.getItem(BESOKSLAGER);
    if (raa) {
      const lagret = JSON.parse(raa) as { id?: unknown; laget?: unknown };
      const utlop = new Date(String(lagret.laget ?? ''));
      utlop.setMonth(utlop.getMonth() + BESOKSSTATISTIKK_MAANEDER);
      if (typeof lagret.id === 'string' && Date.now() < utlop.getTime()) return lagret.id;
    }
    // En ID som lages mens noen er logget inn, hører til den kontoen fra
    // første stund. Uten det kunne den neste som logger inn i nettleseren,
    // overtatt en ID serveren alt har koblet til den forrige.
    const id = nyId();
    window.localStorage.setItem(
      BESOKSLAGER,
      JSON.stringify({ id, laget: new Date().toISOString(), ...(aktivKonto ? { konto: aktivKonto } : {}) }),
    );
    return id;
  } catch {
    return undefined;
  }
}

/**
 * Gir nettleseren en ny besøks-ID neste gang den trengs, uten å slette den gamle.
 *
 * En ID hører til én konto. Kalles når en annen konto enn den ID-en hører til,
 * logger inn – partneren på den felles maskinen. Besøkene under den gamle
 * ID-en hører til kontoen den ble koblet til, og blir der; den som logget inn
 * nå, får sin egen.
 */
export function byttBesoksId(): void {
  try {
    window.localStorage.removeItem(BESOKSLAGER);
  } catch {
    /* Ingenting å gjøre, og ingenting brukeren skal se. */
  }
}

/** Kontoen som er logget inn i denne fanen, så langt vi vet. */
let aktivKonto: string | null = null;

/** Kalles når økta er borte: ved utlogging, og når den har gått ut. */
export function glemAktivKonto(): void {
  aktivKonto = null;
}

/**
 * Noterer hvilken konto ID-en hører til, eller bytter den hvis den hører til
 * en annen.
 *
 * Kalles hver gang økta er kjent: etter innlogging og registrering, når
 * partnerinvitasjonen er godtatt, og når appen starter med en økt fra før.
 * Serveren kobler ID-en til kontoen, men nettleseren må selv vite det, ellers
 * ville den som logger inn etter at forrige økt gikk ut – uten at noen logget
 * ut – fortsatt sende den førstes ID.
 *
 * Utlogging bytter ikke ID-en. Den som logger ut og kommer tilbake, er fortsatt
 * kunden – og skal ikke telles som en ny besøkende som snudde på forsiden.
 */
export function kobleBesoksId(brukerId: string): void {
  aktivKonto = brukerId;
  try {
    const raa = window.localStorage.getItem(BESOKSLAGER);
    if (!raa) return;
    const lagret = JSON.parse(raa) as { id?: unknown; laget?: unknown; konto?: unknown };
    if (typeof lagret.konto === 'string' && lagret.konto !== brukerId) {
      byttBesoksId();
    } else if (lagret.konto !== brukerId) {
      window.localStorage.setItem(BESOKSLAGER, JSON.stringify({ ...lagret, konto: brukerId }));
    }
  } catch {
    /* Ingenting å gjøre, og ingenting brukeren skal se. */
  }
}

/**
 * Slettinger serveren ikke har bekreftet ennå.
 *
 * Et nei skal føre til at besøkene slettes, også når nettet er nede eller
 * serveren svarer med en feil idet noen trykker. Uten denne lista ville ID-en
 * vært borte fra nettleseren før slettingen var gjort, og ingen kunne prøvd
 * igjen. Den ligger her bare til serveren har svart, og brukes ikke til noe
 * annet: å huske den er nødvendig for å gjøre det den besøkende ba om.
 *
 * To slags oppføringer: en besøks-ID, og `konto:<id>` for et nei fra en som
 * var logget inn – som også sletter kontoens besøk fra andre nettlesere. Den
 * siste sendes bare når den samme kontoen er logget inn, så et nei fra den ene
 * aldri sletter den andres besøk på en felles maskin.
 */
const SLETTELAGER = 'vaardag:besokende-slettes';
const KONTO = 'konto:';

function venterPaaSletting(): string[] {
  try {
    const liste = JSON.parse(window.localStorage.getItem(SLETTELAGER) ?? '[]') as unknown;
    return Array.isArray(liste) ? liste.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

function leggTilSletting(oppforing: string): boolean {
  try {
    const venter = venterPaaSletting();
    if (!venter.includes(oppforing)) {
      window.localStorage.setItem(SLETTELAGER, JSON.stringify([...venter, oppforing]));
    }
    return true;
  } catch {
    return false;
  }
}

function slettet(oppforing: string): void {
  try {
    const igjen = venterPaaSletting().filter((annen) => annen !== oppforing);
    if (igjen.length > 0) window.localStorage.setItem(SLETTELAGER, JSON.stringify(igjen));
    else window.localStorage.removeItem(SLETTELAGER);
  } catch {
    /* Da prøver vi igjen neste gang. Serveren tåler å slette det som alt er borte. */
  }
}

function sendSletting(oppforing: string, husket: boolean): void {
  // Kontoen sendes med navn: serveren sletter bare når den samme kontoen er
  // logget inn, og svarer ellers med en feil, så oppføringen blir liggende.
  const kropp = oppforing.startsWith(KONTO)
    ? { konto: oppforing.slice(KONTO.length) }
    : { besokende: oppforing };
  try {
    void fetch('/api/maaling/glem', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify(kropp),
    })
      .then((svar) => {
        if (husket && svar.ok) slettet(oppforing);
      })
      .catch(() => undefined);
  } catch {
    /* Ingenting å gjøre, og ingenting brukeren skal se. */
  }
}

/**
 * Sender slettingene som venter.
 *
 * Kalles ved et nei, og hver gang økta er kjent – så en sletting som feilet,
 * blir gjort neste gang. `innlogget` er kontoen som er logget inn nå; en
 * kontosletting venter til det er den samme som sa nei.
 */
export function sendVentendeSlettinger(innlogget: string | null = null): void {
  for (const oppforing of venterPaaSletting()) {
    if (oppforing.startsWith(KONTO) && oppforing !== KONTO + innlogget) continue;
    sendSletting(oppforing, true);
  }
}

/**
 * Fjerner besøks-ID-en, og ber serveren slette besøkene som hører til den.
 *
 * Kalles når noen sier nei til besøksstatistikk, også når de sa ja før. Et nei
 * som lar det gamle bli liggende, er bare et halvt nei. Er den som sier nei
 * logget inn, slettes også besøkene som er koblet til kontoen fra andre
 * nettlesere – også når denne nettleseren ikke har noen ID akkurat nå.
 */
export function glemBesoksId(konto: string | null = null): void {
  let id: unknown;
  try {
    const raa = window.localStorage.getItem(BESOKSLAGER);
    id = raa ? (JSON.parse(raa) as { id?: unknown }).id : undefined;
    window.localStorage.removeItem(BESOKSLAGER);
  } catch {
    id = undefined;
  }
  const oppforinger = [
    ...(typeof id === 'string' ? [id] : []),
    ...(konto ? [KONTO + konto] : []),
  ];
  for (const oppforing of oppforinger) {
    // Får vi ikke skrive slettelista, sendes slettingen likevel – én gang.
    if (!leggTilSletting(oppforing)) sendSletting(oppforing, false);
  }
  sendVentendeSlettinger(konto);
}

/** Fjerner det som er lagret. Kalles når noen sier nei, eller ombestemmer seg. */
export function glemLagretKampanje(): void {
  try {
    window.localStorage.removeItem(LAGER);
  } catch {
    /* Ingenting å gjøre, og ingenting brukeren skal se. */
  }
}

export type Kampanjefelter = {
  kampanjekilde?: string;
  kampanje?: string;
  /** Selve klikk-ID-en. Følger bare med når den besøkende har sagt ja. */
  klikkid?: string;
  /** At det fantes et annonseklikk. Nok til å sette kanalen, uten samtykke. */
  harKlikkid?: boolean;
  annonsemaalingSamtykke?: boolean;
  /** Verten de kom fra. Avgjør kanalen, og lagres ikke. */
  henvisning?: string;
  /** Besøks-ID-en. Følger bare med når den besøkende har sagt ja til besøksstatistikk. */
  besokende?: string;
};

/**
 * Feltene registreringen skal sende med, uten de tomme.
 *
 * Klikk-ID-en er den eneste som henger på samtykket. `harKlikkid` sendes
 * uansett: kanalen skal kunne si «kampanje» også for den som sa nei, og en
 * bøtte er ikke en identifikator.
 */
export function kampanjefelter(): Kampanjefelter {
  const k = kampanje();
  const h = henvisning();
  const samtykke = harAnnonsemaaling();
  const besokende = besoksId();
  return {
    ...(k.kilde ? { kampanjekilde: k.kilde } : {}),
    ...(k.navn ? { kampanje: k.navn } : {}),
    ...(k.klikkid ? { harKlikkid: true } : {}),
    ...(k.klikkid && samtykke ? { klikkid: k.klikkid } : {}),
    ...(samtykke ? { annonsemaalingSamtykke: true } : {}),
    ...(h ? { henvisning: h } : {}),
    ...(besokende ? { besokende } : {}),
  };
}

/** Siden fanen landet på, hvis det var forsiden eller prislista. */
let landing: 'forside' | 'priser' | null = null;

/**
 * Om landingen alt har gjort sin nytte for innloggingen.
 *
 * Én landing er én kunde på vei inn, ikke én per innlogging i fanen. Logger
 * noen inn, ut og inn igjen – eller kom de innlogget, og logger ut og inn –
 * skal ikke det telles som flere kunder som gikk via forsiden.
 */
let landingBrukt = false;

/** Kalles etter en innlogging og en utlogging. Se `landingBrukt`. */
export function brukOppLanding(): void {
  landingBrukt = true;
}

/**
 * Feltene innloggingen skal sende med, uten de tomme.
 *
 * Landingen er det som lar serveren telle kunder som går via forsiden eller
 * prislista for å logge inn. Den huskes i minnet så lenge fanen lever, som
 * kampanjen, og lagres aldri. Kanalen følger med for at tellingen skal ha en,
 * og besøks-ID-en når den besøkende har sagt ja til besøksstatistikk.
 */
export function innloggingsfelter(): {
  landing?: 'forside' | 'priser';
  henvisning?: string;
  kampanjekilde?: string;
  harKlikkid?: boolean;
  besokende?: string;
} {
  const besokende = besoksId();
  const aktiv = landingBrukt ? null : landing;
  const k = aktiv ? kampanje() : {};
  const h = aktiv ? henvisning() : undefined;
  return {
    ...(aktiv ? { landing: aktiv } : {}),
    ...(h ? { henvisning: h } : {}),
    ...(k.kilde ? { kampanjekilde: k.kilde } : {}),
    ...(k.klikkid ? { harKlikkid: true } : {}),
    ...(besokende ? { besokende } : {}),
  };
}

/**
 * Stegene som ble meldt i denne fanen før det fantes et ja.
 *
 * Boksen om informasjonskapsler kommer fram idet sida lastes, og da er
 * landingen alt meldt – uten ID, fordi samtykket ikke fantes ennå. Uten denne
 * lista ville det første besøket til alle som sier ja, mangle i statistikken,
 * og «kom igjen før de registrerte seg» blitt ett besøk for lavt for alle.
 */
const meldtUtenId = new Set<Klientsteg>();

/**
 * Melder stegene i denne fanen en gang til, nå med besøks-ID.
 *
 * Kalles når noen sier ja til besøksstatistikk. Telleren har dem alt, og
 * serveren teller dem ikke igjen.
 */
export function meldEtterSamtykke(): void {
  const steg = [...meldtUtenId];
  meldtUtenId.clear();
  for (const navn of steg) meld(navn, { alleredeTalt: true });
}

/**
 * Melder fra om at et steg ble nådd.
 *
 * `keepalive` fordi meldingen ofte sendes idet brukeren navigerer videre; uten
 * den ville nettleseren avbrutt kallet, og forsiden hadde sett mindre besøkt ut
 * enn den er. Feiler den, feiler den stille: en telling som mangler skal aldri
 * bli noe den besøkende merker.
 */
export function meld(steg: Klientsteg, { alleredeTalt = false } = {}): void {
  const k = kampanje();
  const h = henvisning();
  const medId = BESOKSSTEG.includes(steg);
  const besokende = medId ? besoksId() : undefined;
  if (medId && !besokende) meldtUtenId.add(steg);
  // Et steg som alt er talt, sendes bare for å få ID-en med. Uten ID er det
  // ingenting å sende.
  if (alleredeTalt && !besokende) return;
  try {
    void fetch('/api/maaling', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        steg,
        ...(k.kilde ? { kampanjekilde: k.kilde } : {}),
        ...(k.navn ? { kampanje: k.navn } : {}),
        ...(k.klikkid ? { harKlikkid: true } : {}),
        ...(h ? { henvisning: h } : {}),
        ...(besokende ? { besokende } : {}),
        ...(alleredeTalt ? { alleredeTalt: true } : {}),
      }),
    })
      .then((svar) => {
        // ID-en hører til en annen konto enn den som er logget inn – for
        // eksempel fordi en annen fane ikke fikk med seg hvem som logget inn.
        // Serveren har ikke lagret besøket; neste melding får en ny ID.
        if (besokende && svar.status === 409) byttBesoksId();
      })
      .catch(() => undefined);
  } catch {
    /* Ingenting å gjøre, og ingenting brukeren skal se. */
  }
}

/**
 * Toppen av trakten: forsiden og prislista.
 *
 * De to er ikke to steg, men to dører inn i det samme første steget – og
 * derfor meldes bare den første av dem. Uten det ville den som kommer inn på
 * forsiden og klikker «Priser» i menyen telt to ganger, og toppen av trakten
 * vokst med hvor mange som ser seg om, ikke med hvor mange som kom.
 *
 * Landingen lever i minnet, like lenge som fanen. Den som laster siden på nytt
 * telles igjen. For dette lagres ingenting på maskinen til den besøkende – en
 * teller er ingen grunn til å legge noe igjen hos noen, og det er nettopp det
 * som gjør at tellingen ikke trenger å spørre om lov.
 *
 * Til gjengjeld forsvant en dobbelttelling som alt var der: forsiden meldte
 * fra hver gang den ble tegnet, også når man klikket seg tilbake til den.
 */
export function meldLanding(steg: 'forside' | 'priser'): void {
  if (landing) return;
  landing = steg;
  meld(steg);
}

/** Bare for testene: glemmer den huskede kampanjen og henvisningen. */
export function glemKampanje(): void {
  husket = null;
  henvist = null;
}

/** Bare for testene: glemmer at toppen av trakten er meldt. */
export function glemLanding(): void {
  landing = null;
  landingBrukt = false;
  aktivKonto = null;
  meldtUtenId.clear();
}
